import { useContext, useEffect, useMemo, useState } from "react";
import { Breadcrumb, Button, Card, Col, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import SimpleBar from "simplebar-react";
import { AppContext } from "../../../common";
import BackButton from "../../../common/filter-table/BackButton";
import { Private_Routes } from "../../../constants";
import {
  ApproveOverTimesModal,
  ApproveOverTimesModalProps,
} from "../components/modals/ApproveOverTimesModal";
import {
  RejectOverTimesModal,
  RejectOverTimesModalProps,
} from "../components/modals/RejectOverTimesModal";
import PayrollNoveltyTable from "../components/tables/PayrollNoveltyTable";
import { RenderTabs } from "../components/tabs-panel/RenderTabs";
import { useLiquidatedOvertimeHours } from "../hooks/useLiquidatedOvertimeHours";
import {
  HourSummaryStatesEnum,
  LiquidatedOvertimeHourModel,
} from "../models/liquidateOvertimeHours";
import { RequestLiquidatedOvertimeHourRequest } from "../models/requests";
import "../styles/style.css";

export const ManagePayrollNovelty = () => {
  const { startDateScheduling, endDateScheduling, selectedEmployees } =
    useContext(AppContext);

  const { tab } = useParams<{ tab: string }>();

  const { getLiquidatedOvertimeHours, isLoading } =
    useLiquidatedOvertimeHours();

  const [liquidateOverTimeHoursList, setLiquidateOverTimeHoursList] = useState<
    LiquidatedOvertimeHourModel[]
  >([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedList, setSelectedList] = useState<
    LiquidatedOvertimeHourModel[]
  >([]);

  const [approveOverTimesModal, setApproveOverTimesModal] =
    useState<ApproveOverTimesModalProps>({} as ApproveOverTimesModalProps);

  const [rejectOverTimesModal, setRejectOverTimesModal] =
    useState<RejectOverTimesModalProps>({} as RejectOverTimesModalProps);

  const handleChangeSelected = (item: LiquidatedOvertimeHourModel) => {
    setSelectedList((prevSelectedList) => {
      const updatedList = new Set(prevSelectedList);
      if (updatedList.has(item)) {
        updatedList.delete(item);
      } else {
        updatedList.add(item);
      }

      return Array.from(updatedList);
    });
  };

  const fetchLiquidatedOvertimeHours = async () => {
    try {
      const data: RequestLiquidatedOvertimeHourRequest = {
        employeeIds: selectedEmployees.map((e) => Number(e.id)),
        endDate: `${endDateScheduling}T00:00:00`,
        startDate: `${startDateScheduling}T00:00:00`,
      };

      const response = await getLiquidatedOvertimeHours(data);

      if ([400, 500, 404].includes(response?.response?.status)) {
        setErrorMessage(
          response?.response?.data ??
            "Ocurrió un error al consultar las horas extras",
        );
      } else {
        setLiquidateOverTimeHoursList(response);

        setErrorMessage("");
      }

      setSelectedList([]);
    } catch (error) {
      setErrorMessage("Ocurrió un error al consultar las horas extras");
    }
  };

  useEffect(() => {
    fetchLiquidatedOvertimeHours();
  }, [startDateScheduling, endDateScheduling, selectedEmployees]);

  const groupedData = useMemo(() => {
    if (!liquidateOverTimeHoursList.length || !tab) return {};

    let result: Record<string, LiquidatedOvertimeHourModel[]> = {};

    switch (tab) {
      case "employee":
        result = liquidateOverTimeHoursList.reduce<
          Record<string, LiquidatedOvertimeHourModel[]>
        >((acc, item) => {
          const key = `${item.employeeId}`;
          if (!acc[key]) acc[key] = [];
          acc[key].push(item);
          return acc;
        }, {});
        break;

      case "date":
        result = liquidateOverTimeHoursList.reduce<
          Record<string, LiquidatedOvertimeHourModel[]>
        >((acc, item) => {
          const key = item.date;
          if (!acc[key]) acc[key] = [];
          acc[key].push(item);
          return acc;
        }, {});
        break;

      case "status":
        result = liquidateOverTimeHoursList.reduce<
          Record<string, LiquidatedOvertimeHourModel[]>
        >((acc, item) => {
          const key =
            item.status !== null
              ? HourSummaryStatesEnum[item.status] || "Undefined"
              : "Undefined";

          if (!acc[key]) acc[key] = [];
          acc[key].push(item);
          return acc;
        }, {});
        break;

      default:
        console.warn(`Tipo de agrupamiento "${tab}" no válido`);
        break;
    }

    return result;
  }, [liquidateOverTimeHoursList, tab]);

  const onCloseApproveOverTimesModal = () => {
    setApproveOverTimesModal((prev) => ({ ...prev, isOpen: false }));
  };

  const openApproveOverTimesModal = () => {
    setApproveOverTimesModal({
      isOpen: true,
      onClose: onCloseApproveOverTimesModal,
      reHidrate: fetchLiquidatedOvertimeHours,
      data: selectedList,
    });
  };

  const onCloseRejectOverTimesModal = () => {
    setRejectOverTimesModal((prev) => ({ ...prev, isOpen: false }));
  };

  const openRejectOverTimesModal = () => {
    setRejectOverTimesModal({
      isOpen: true,
      onClose: onCloseRejectOverTimesModal,
      data: selectedList,
      reHidrate: fetchLiquidatedOvertimeHours,
    });
  };

  return (
    <>
      {approveOverTimesModal.isOpen && (
        <ApproveOverTimesModal {...approveOverTimesModal} />
      )}

      {rejectOverTimesModal.isOpen && (
        <RejectOverTimesModal {...rejectOverTimesModal} />
      )}

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="row ">
              <div className="col-12 p-3 py-0">
                <Helmet>
                  <title>Flujos de aprobación</title>
                </Helmet>

                <Breadcrumb>
                  <Breadcrumb.Item href={"/"}>Inicio</Breadcrumb.Item>
                  <Breadcrumb.Item href={Private_Routes.HOME}>
                    Aprobar y rechazar horas extras
                  </Breadcrumb.Item>
                </Breadcrumb>

                <Row>
                  <Col sm={12}>
                    <Card>
                      <Card.Body className="">
                        {isLoading && <p>Cargando tiempos extras...</p>}

                        {!isLoading && errorMessage !== "" ? (
                          <div className="alert alert-danger ">
                            {errorMessage}
                          </div>
                        ) : liquidateOverTimeHoursList &&
                          liquidateOverTimeHoursList?.length == 0 ? (
                          <p className="text-muted">
                            No hay empleados con horas extras pendientes por
                            aprobar
                          </p>
                        ) : (
                          <div className="card-body">
                            <RenderTabs
                              setSelectedList={setSelectedList}
                              activeTab={tab ?? "employee"}
                            />
                            <SimpleBar direction="rtl" className="SimpleBar">
                              <div className="tab-content text-muted">
                                {["employee", "date", "status"].map(
                                  (tabKey, index) => (
                                    <div
                                      id={`tab-${tabKey}`}
                                      className={`tab-pane ${
                                        tab === tabKey ? "active" : ""
                                      }`}
                                      key={tabKey}
                                    >
                                      <PayrollNoveltyTable
                                        dataList={groupedData}
                                        tab={tabKey}
                                        selectedList={selectedList}
                                        onChangeSelection={handleChangeSelected}
                                        reHidrate={fetchLiquidatedOvertimeHours}
                                      />
                                    </div>
                                  ),
                                )}
                              </div>
                            </SimpleBar>
                          </div>
                        )}
                      </Card.Body>

                      <Card.Footer>
                        <div className="d-flex gap-2">
                          <BackButton to={Private_Routes.HOME} />
                          <Button
                            type={"button"}
                            variant="success"
                            disabled={isLoading || selectedList.length == 0}
                            onClick={openApproveOverTimesModal}
                          >
                            Aprobar seleccionadas ({selectedList?.length})
                          </Button>
                          <Button
                            type={"button"}
                            variant="danger"
                            disabled={isLoading || selectedList.length == 0}
                            onClick={openRejectOverTimesModal}
                          >
                            Rechazar seleccionadas ({selectedList?.length})
                          </Button>
                        </div>
                      </Card.Footer>
                    </Card>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
